/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { MenuList } from '@coursera/cds-core';

import { MenuContext } from './MenuContext';

const styles = {
  invisibleDivider: css`
    width: 100%;
    height: 4px;
  `,
  iconContainer: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-right: var(--cds-spacing-150);
  `,
  contentContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
};

interface ActionMenuItemBase {
  /**
   * Item label
   */
  label: React.ReactNode;

  /**
   * Is item selected
   */
  selected?: boolean;
  /**
   * Disabled status, default is whether isHeader is provided or not
   */
  disabled?: boolean;
  /**
   * CDS Icon
   */
  icon?: React.ReactNode;
  /**
   * CDS Tag
   */
  tag?: React.ReactNode;
  /**
   * Additional classnames if needed
   */
  className?: string;
  /**
   * Optional test selector
   */
  ['data-testid']?: string;

  /**
   * Onclick callback function
   */
  onClick: React.EventHandler<React.MouseEvent>;
}

const ActionMenuItem = React.forwardRef<HTMLButtonElement, ActionMenuItemBase>((props, ref) => {
  const { label, disabled, selected, icon, tag, className, 'data-testid': dataTestId, onClick } = props;

  const { onClose } = useContext(MenuContext);

  const handleClick = (event: React.MouseEvent) => {
    if (onClick) {
      onClick(event);
      onClose();
    }
  };

  const prefix = icon && <span css={styles.iconContainer}>{icon}</span>;

  return (
    <MenuList.Item
      prefix={prefix}
      role="menuitemradio"
      ref={ref}
      disabled={disabled}
      checked={selected}
      className={className}
      onClick={handleClick}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <div css={styles.contentContainer}>
        {label}
        {tag}
      </div>
    </MenuList.Item>
  );
});

export default ActionMenuItem;
